import { isEqual } from 'lodash';
import Vue from 'vue';
import { createFetchAndStoreUrls, removeQueryParams } from 'vue/router/env-helpers';


const DEBUG = globals.config.debug;


/**
 * Compile full fetch api endpoint by using params and query objects
 * @param {string} fetchUrl - A base string, used as a template
 * @param {Object} params - A params object that fills the params gap in the `fetchUrl` template string
 * @param {Object} query - A query object that is being appended to the `fetchUrl` template string
 * @return {string} - The fully compiled fetch url
 */
function compileFetchUrl(fetchUrl, params, query) {
    // Leverage params object to compile fetch url
    fetchUrl = Object.entries(params).reduce((url, [key, val]) => {
        return url.replace(`:${ key }`, val);
    }, fetchUrl);

    // Leverage query object to compile fetch url
    const searchParams = new URLSearchParams();
    Object.entries(query).forEach(([key, val]) => {
        searchParams.append(key, val);
    });
    const search = searchParams.toString();

    return `${ fetchUrl }${ search.length ? '?' : '' }${ search }`;
}

/**
 * Register an api url and create a function through which api data can be accessed (via cache or api call)
 * @param {Object} store - The vuex store object
 * @param {Object} fetch - A fetch endpoint definition
 * @param {string} fetch.url - The basic url prior to any manipulation
 * @param {Object} fetch.params - A list of base params that are being merged with specific params, later on
 * @param {Object} fetch.query - A query object that is being merged with the specific query object, later on
 * @return {Function} - Return a handler function for accessing fetch data via a returned promise
 */
export function setEndpoint(store, { url, params: presetParams, query: presetQuery }) {
    return function fetch({ params: routeParams, query: routeQuery, response }) {
        // Merge params and query with route presets
        const params = Object.assign({}, presetParams, routeParams);
        const query = Object.assign({}, presetQuery, routeQuery);

        // Create compiled endpoint url
        const [fetchKey, storageKey] = createFetchAndStoreUrls(store, compileFetchUrl, url, params, query);

        // Push available data into the store's endpoint address
        if (response) {
            DEBUG && console.log(`[router/helpers] - Saving prefetched data for URL '${ storageKey }'`);
            return store.dispatch('setRouteData', { key: storageKey, value: response });
        }

        // Fetch data from compiled endpoint
        DEBUG && console.log(`[router/helpers] - Fetching data for URL '${ fetchKey }'`);
        return store.dispatch('getRouteData', { storageKey, fetchKey });
    };
}

/**
 * Route config creation helper
 * @param {Object} store - The vuex store object
 * @param {Array} records - A route record array describing a list of available routes
 * @param {Array} parents - The records' parental hierarchy, the first item being the oldest
 * @return {Array} - Gives back the enhanced route records, ready for being used by `vue-router`
 */
export function decorateRecords(store, records = [], parents = []) {
    /**
     * Create a route descriptor ready for consumation by `vue-router`
     * @param {Object} record - The base config object received from the server
     * @param {string} record.name - The page's name
     * @param {string} record.path - The base path prior to any manipulation by params or query
     * @param {string} record.component - The component name used by the route
     * @param {Array} record.children - A list of child route definitions
     * @param {string|Array<string>} record.alias - A list of child route definitions
     * @param {string} record.redirect - A list of child route definitions
     * @param {Object} record.meta - An object of properties that shall be available later on in the route
     * @param {Object} record.api - The api definition containing all necessary info to fetch and store this route's data
     * @param {Object} record.api.fetch - The fetch definition used to create the api endpoint
     * @param {Object} record.api.fetched - A data object, containing prefetched data from the server
     */
    return records.map(({ name, path, component, children, alias, redirect, meta = {}, api = {} }) => {
        let { fetch, fetched } = api;
        let result = { name, path, meta, alias, redirect };
        let hierarchy = parents.slice();

        // Add the current route to its own hierarchy
        hierarchy.push(name);

        // Recursively transform child records
        result.children = children && decorateRecords(store, children, hierarchy);

        if (!redirect && !alias) {
            let page = `page-${ component }`;
            let fetchMethod = setEndpoint(store, fetch);

            // Invoke the vue component that is used for the route outlet
            result.component = Vue.component(page);

            // Expose all properties that shall be available within the route component as `this.$route.meta`
            Object.assign(result.meta, {
                page, // String reference to the vue component's template
                hierarchy, // String tuple of this route's parents including itself as the last index
                api: Object.assign({ fetch: fetchMethod }, fetch),
            });

            // Fetched data may already be present when initializing, store it in that case
            if (fetched) {
                result.meta.api.fetch(fetched);
            }
        }

        // Remove undefined object entries
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // eslint-disable-line no-undefined

        return result;
    });
}

/**
 * Compare two route definitions, ignoring irrelevant information such as hash
 * @param {Object} to - A route record describing the previous location
 * @param {Object} from - A route record describing the next location
 */
export function compareRecords(to, from) {
    const hasSameName = to.name === from.name;
    const hasSameQuery = isEqual(removeQueryParams(to.query), removeQueryParams(from.query));
    const hasSameParams = isEqual(to.params, from.params);

    return hasSameName && hasSameQuery && hasSameParams;
}



// WEBPACK FOOTER //
// ./sib-website/assets/src/js/app/vue/router/api-helpers.js